import { ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, input, output, signal, viewChild } from '@angular/core';

import { SkIconComponent } from '@mercadoni/stark-ui';

import { STATUS_PAGE_URL, StatusPageService } from '../../core/services/status-page.service';
import { ClickOutsideDirective } from '../../shared/directives/click-outside.directive';

/** Spanish copy of the floating button and its quick menu. */
const FAB_LABELS = {
  open: 'Abrir menú rápido',
  menuTitle: 'Menú rápido',
  assistant: 'Asistente LeapBot',
  assistantHint: 'Pregunta o crea un ticket',
  unavailable: 'No disponible temporalmente',
  status: 'Estado de sistemas',
  statusLink: 'Ver página de estado completa',
  loading: 'Cargando...',
  allOperational: 'Todos los sistemas operativos',
  incidentBadge: 'Hay incidentes en los sistemas',
  maintenanceBadge: 'Hay mantenimientos programados',
} as const;

/**
 * Floating button at the bottom-right of the screen with a quick menu: the LeapBot assistant and the
 * system status (a summary of the Instaleap status page and a link to it). It never shows while the
 * chat window is open, and shows a badge while there is an incident or a maintenance.
 */
@Component({
  selector: 'nf-leapbot-fab',
  imports: [ClickOutsideDirective, SkIconComponent],
  templateUrl: './leapbot-fab.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NfLeapBotFabComponent {
  /** Whether the user may use the chat; when false nothing is shown and the status is not read. */
  readonly visible = input<boolean>(false);

  /** Whether LeapBot is configured; when false its menu entry is disabled. */
  readonly leapBotEnabled = input<boolean>(true);

  /** Whether the chat window is open (the button hides while it is). */
  readonly chatOpen = input<boolean>(false);

  /** Emitted when the user chooses the LeapBot assistant. */
  readonly openChat = output<void>();

  private readonly statusPage = inject(StatusPageService);
  private readonly menu = viewChild<ElementRef<HTMLElement>>('menu');
  private readonly button = viewChild<ElementRef<HTMLButtonElement>>('fabButton');

  protected readonly labels = FAB_LABELS;
  protected readonly statusUrl = STATUS_PAGE_URL;
  protected readonly menuOpen = signal(false);

  protected readonly shown = computed(() => this.visible() && !this.chatOpen());

  /** Badge on the button: amber for maintenance only, red for any incident, none when all is operational. */
  protected readonly badge = computed((): 'maintenance' | 'incident' | null => {
    const worst = this.statusPage.summary()?.worst ?? 'operational';
    if (worst === 'operational') return null;
    return worst === 'maintenance' ? 'maintenance' : 'incident';
  });

  protected readonly badgeText = computed((): string | null => {
    const badge = this.badge();
    if (badge === null) return null;
    return badge === 'incident' ? this.labels.incidentBadge : this.labels.maintenanceBadge;
  });

  /** Line under `Estado de sistemas`; `null` after a failure so only the link remains. */
  protected readonly statusLine = computed((): string | null => {
    const state = this.statusPage.state();
    if (state === 'error') return null;
    const summary = this.statusPage.summary();
    if (state !== 'ready' || !summary) return this.labels.loading;
    return summary.problems === 0 ? this.labels.allOperational : `${summary.problems} con problemas`;
  });

  constructor() {
    // The status is read only while the button is on screen for a user who can chat.
    effect((onCleanup) => {
      if (!this.visible()) return;
      onCleanup(this.statusPage.watch());
    });
    // Opening the menu moves focus to its first enabled entry.
    effect(() => {
      if (this.menuOpen()) setTimeout(() => this.entries()[0]?.focus());
    });
  }

  protected toggleMenu(): void {
    this.menuOpen.update((open) => !open);
  }

  protected closeMenu(returnFocus: boolean): void {
    if (!this.menuOpen()) return;
    this.menuOpen.set(false);
    if (returnFocus) this.button()?.nativeElement.focus();
  }

  protected chooseAssistant(): void {
    if (!this.leapBotEnabled()) return;
    this.closeMenu(false);
    this.openChat.emit();
  }

  protected onMenuKeydown(event: KeyboardEvent): void {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const entries = this.entries();
    if (entries.length === 0) return;
    const current = entries.indexOf(document.activeElement as HTMLElement); // focus is inside the menu while it is open
    const step = event.key === 'ArrowDown' ? 1 : -1;
    entries[(current + step + entries.length) % entries.length].focus();
  }

  private entries(): HTMLElement[] {
    const root = this.menu()?.nativeElement;
    return root ? Array.from(root.querySelectorAll<HTMLElement>('[role="menuitem"]:not([disabled])')) : [];
  }
}
